<template>
  <div class="text-sm">
    <div v-if="formattedHours && formattedHours.length > 0">
      <ul class="space-y-1">
        <li v-for="(day, index) in formattedHours" :key="index" class="flex justify-between">
          <span class="font-medium text-gray-700">{{ day.day }}:</span>
          <span :class="['text-gray-600', day.isToday ? 'font-bold text-primary' : '']">
            {{ day.hours }}
          </span>
        </li>
      </ul>
    </div>
    <div v-else class="text-gray-500 italic">
      {{ emptyText }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'BusinessHoursDisplay',
  props: {
    hoursData: { // Expects an object or an array of strings
      type: [Object, Array],
      default: () => ({})
    },
    emptyText: {
      type: String,
      default: 'Hours not available'
    }
  },
  computed: {
    formattedHours() {
      if (!this.hoursData) return [];

      const daysOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
      const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });

      if (Array.isArray(this.hoursData)) {
        // Handle simple array of strings like ["Monday: 9 AM - 5 PM", ...]
        return this.hoursData.map(entry => {
          const parts = entry.split(/:(.*)/s); // Split only on the first colon
          const day = parts[0]?.trim();
          return {
            day: day,
            hours: parts[1]?.trim() || 'Closed',
            isToday: day === today
          };
        }).sort((a, b) => daysOrder.indexOf(a.day) - daysOrder.indexOf(b.day));
      }
      
      if (typeof this.hoursData === 'object') {
        // Handle object like { "Monday": "9 AM - 5 PM", ... } or Google's periods format
        if (this.hoursData.periods) { // Google My Business format
          const weeklyHours = {};
          daysOrder.forEach(d => weeklyHours[d] = 'Closed');

          this.hoursData.periods.forEach(period => {
            if (period.open && period.close) {
              const openDay = daysOrder[period.open.day];
              const openTime = `${String(period.open.hours).padStart(2, '0')}:${String(period.open.minutes).padStart(2, '0')}`;
              const closeDay = daysOrder[period.close.day]; // Note: closeDay might be different for overnight hours
              const closeTime = `${String(period.close.hours).padStart(2, '0')}:${String(period.close.minutes).padStart(2, '0')}`;
              
              if (weeklyHours[openDay] === 'Closed') {
                weeklyHours[openDay] = `${openTime} - ${closeTime}`;
              } else {
                weeklyHours[openDay] += `, ${openTime} - ${closeTime}`; 
              }
              // This doesn't handle complex cases like closing on a different day perfectly yet.
            }
          });
          return Object.entries(weeklyHours).map(([day, hours]) => ({
            day,
            hours,
            isToday: day === today
          })).sort((a, b) => daysOrder.indexOf(a.day) - daysOrder.indexOf(b.day));
        }

        // Standard object format
        return Object.entries(this.hoursData)
          .map(([day, hours]) => ({
            day: this.formatDayKey(day),
            hours: hours || 'Closed',
            isToday: this.formatDayKey(day) === today
          }))
          .sort((a,b) => daysOrder.indexOf(a.day) - daysOrder.indexOf(b.day));
      }
      return [];
    }
  },
  methods: {
    formatDayKey(key) {
      return key.charAt(0).toUpperCase() + key.slice(1).toLowerCase();
    }
  }
};
</script>

<style scoped>
/* Scoped styles */
</style> 